"use client";

import { BookADemoModal } from "@/app/(public)/shared/components/BookADemoModal";
import LogoWall from "@/app/(public)/shared/components/LogoWall";
import { UnifiedModal, useModalManager } from "@/app/(public)/shared/components/UnifiedModal";
import "@/app/(public)/shared/tokens/spacing.css";
import { Button } from "@/app/(public)/shared/ui/Button";
import { ShineBorder } from "@/app/(public)/shared/ui/ShineBorder";
import { motion } from "motion/react";
import Link from "next/link";

export default function Hero() {
  const { openModal, closeModal, isModalOpen } = useModalManager();

  return (
    <div className="w-full flex justify-center">
      <div
        className="flex w-full max-w-[1200px] flex-col justify-center gap-12 pb-28 md:gap-20"
        style={{
          paddingTop: "calc(var(--promo-offset, 0px) + var(--mobile-nav-height, 3.5rem) + 1.5rem)",
        }}
      >
        <div className="self-stretch flex flex-col justify-start items-start lg:items-center gap-6 md:gap-9">
          <motion.div
            className="group relative px-4 py-2 rounded-[100px] outline outline-2 outline-offset-[-2px] outline-fuchsia-950 inline-flex justify-center items-center gap-2.5 overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-md hover:shadow-fuchsia-500/25"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 0.6,
              delay: 1.2, // Same as buttons - appear last
              ease: [0.16, 1, 0.3, 1],
            }}
            onClick={() => {
              window.open("/create", "_blank");
            }}
          >
            <motion.div
              whileHover={{
                transition: { duration: 0.3 },
              }}
            >
              <ShineBorder
                borderWidth={2}
                duration={16}
                shineColor={["#8b5cf6", "#c084fc", "#ec4899", "#6366f1"]}
                className="rounded-[100px] hover:animate-pulse"
              />
            </motion.div>
            <div className="relative z-10 justify-start text-landing-primary text-body font-medium leading-relaxed transition-colors duration-200 group-hover:text-fuchsia-600">
              Create an Event 🎉
            </div>
          </motion.div>
          <div className="flex flex-col justify-start items-start lg:items-center gap-4 md:gap-6">
            <div className="w-full max-w-[692px] text-left lg:text-center justify-start text-landing-primary text-display-sm">
              {["The", "first", "AI", "for", "corporate", "events"].map((word, index) => (
                <motion.span
                  key={index}
                  className="inline-block mr-[0.3em]"
                  initial={{ opacity: 0, y: 30, filter: "blur(10px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.1, // 6 words × 0.1s = 0.6s total (back to original)
                    ease: [0.16, 1, 0.3, 1],
                  }}
                >
                  {word}
                </motion.span>
              ))}
            </div>
            <motion.div
              className="w-full max-w-[420px] text-left lg:text-center justify-start text-landing-secondary text-body-xl font-medium leading-loose"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.6,
                delay: 0.75, // Wait for hero words to finish (0.6s) + 0.15s gap
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              From venue sourcing to day-of coordination, we handle it all so you don't have to.
            </motion.div>
          </div>
          <motion.div
            className="flex flex-wrap justify-start lg:justify-center items-start lg:items-center gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{
              duration: 0.6,
              delay: 1.2, // Much longer delay to appear last
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            <Link href="/create">
              <Button variant="primary" color="primary" size="lg" arrow>
                Try our platform
              </Button>
            </Link>
            <Button variant="outline" color="neutral" size="lg" onClick={() => openModal("demo")}>
              Book a Demo
            </Button>
          </motion.div>
        </div>
        {/* LogoWall - separate container to prevent overflow issues */}
        <div className="w-full flex justify-center overflow-hidden">
          <div
            className="cursor-pointer"
            onClick={() => {
              window.open("/case-studies", "_blank");
            }}
          >
            <LogoWall variant="hero-rows" type="hero" rows={3} logosPerRow={7} />
          </div>
        </div>
      </div>

      {/* Unified Modals */}
      <BookADemoModal isOpen={isModalOpen("demo")} onClose={() => closeModal("demo")} />

      <UnifiedModal
        type="content"
        isOpen={isModalOpen("video")}
        onClose={() => closeModal("video")}
        title="Watch Demo Video"
        subtitle="See Nowadays in action"
        size="lg"
        closeOnBackdrop={true}
        closeOnEscape={true}
      >
        <div className="p-4">
          <iframe
            className="w-full h-[70vh] rounded-xl"
            src="https://www.youtube.com/embed/SS2bcogWkeE"
            title="Nowadays - AI for Event Planning (YC S23)"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          ></iframe>
        </div>
      </UnifiedModal>
    </div>
  );
}
